import type { Awareness } from 'y-protocols/awareness';
import {
  COMMENT_AWARENESS_FIELD,
  COMMENT_EVENT_SEEN_LIMIT,
  CommentRealtimeEvent,
  trimCommentEventQueue,
} from '../models/CommentRealtime';

type AwarenessUpdateHandler = (arg: {
  added: number[];
  updated: number[];
  removed: number[];
}, origin: any) => void;

type Listener = (event: CommentRealtimeEvent) => void;

/**
 * CommentRealtimeChannel manages comment-focused awareness messages.
 * It publishes lightweight descriptors to the shared awareness channel and
 * notifies subscribers when remote events arrive.
 */
export class CommentRealtimeChannel {
  private readonly awareness: Awareness;
  private readonly clientId: number;
  private readonly listeners = new Set<Listener>();
  private readonly seenEventIds = new Set<string>();
  private awarenessHandler: AwarenessUpdateHandler | null = null;

  constructor(awareness: Awareness) {
    this.awareness = awareness;
    this.clientId = awareness.clientID;
    this.seedSeenEvents();
    this.attachAwarenessHandler();
  }

  publish(event: CommentRealtimeEvent): void {
    const current = this.awareness.getLocalState() ?? {};
    const existing = Array.isArray(current[COMMENT_AWARENESS_FIELD])
      ? [...(current[COMMENT_AWARENESS_FIELD] as CommentRealtimeEvent[])]
      : [];

    const nextEvents = trimCommentEventQueue(
      [
        ...existing,
        {
          ...event,
          originClientId: this.clientId,
        },
      ],
    );

    this.awareness.setLocalStateField(COMMENT_AWARENESS_FIELD, nextEvents);
    this.trackSeenIds(nextEvents);
  }

  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  destroy(): void {
    if (this.awarenessHandler) {
      this.awareness.off('update', this.awarenessHandler as any);
      this.awarenessHandler = null;
    }
    this.listeners.clear();
    this.seenEventIds.clear();
  }

  private attachAwarenessHandler(): void {
    this.awarenessHandler = ({ added, updated }) => {
      const changed = [...added, ...updated];
      if (changed.length === 0) {
        return;
      }

      const states = this.awareness.getStates();
      const inbound: CommentRealtimeEvent[] = [];

      for (const client of changed) {
        if (client === this.clientId) {
          continue;
        }
        const state = states.get(client);
        if (!state) {
          continue;
        }
        const events = state[COMMENT_AWARENESS_FIELD];
        if (!Array.isArray(events)) {
          continue;
        }

        for (const candidate of events) {
          if (!isCommentRealtimeEvent(candidate)) {
            continue;
          }
          if (this.seenEventIds.has(candidate.id)) {
            continue;
          }
          this.seenEventIds.add(candidate.id);
          inbound.push(candidate);
        }
      }

      if (inbound.length === 0) {
        return;
      }

      this.trimSeenIds();
      for (const event of inbound) {
        this.emit(event);
      }
    };

    this.awareness.on('update', this.awarenessHandler as any);
  }

  private emit(event: CommentRealtimeEvent): void {
    if (event.originClientId === this.clientId) {
      return;
    }
    for (const listener of this.listeners) {
      listener(event);
    }
  }

  private seedSeenEvents(): void {
    const states = this.awareness.getStates();
    for (const [, state] of states.entries()) {
      const events = state?.[COMMENT_AWARENESS_FIELD];
      if (!Array.isArray(events)) {
        continue;
      }
      this.trackSeenIds(events);
    }
    this.trimSeenIds();
  }

  private trackSeenIds(events: CommentRealtimeEvent[]): void {
    for (const event of events) {
      if (isCommentRealtimeEvent(event)) {
        this.seenEventIds.add(event.id);
      }
    }
    this.trimSeenIds();
  }

  private trimSeenIds(): void {
    if (this.seenEventIds.size <= COMMENT_EVENT_SEEN_LIMIT) {
      return;
    }
    const ids = Array.from(this.seenEventIds).slice(-COMMENT_EVENT_SEEN_LIMIT);
    this.seenEventIds.clear();
    for (const id of ids) {
      this.seenEventIds.add(id);
    }
  }
}

function isCommentRealtimeEvent(value: unknown): value is CommentRealtimeEvent {
  if (!value || typeof value !== 'object') {
    return false;
  }
  const candidate = value as Record<string, unknown>;
  return (
    typeof candidate.id === 'string' &&
    typeof candidate.type === 'string' &&
    typeof candidate.diagramId === 'string' &&
    typeof candidate.threadRootId === 'string' &&
    typeof candidate.commentId === 'string' &&
    typeof candidate.timestamp === 'number'
  );
}
